import PropTypes from 'prop-types';
import {
  Button, Checkbox, Chip, Dialog, DialogActions, DialogContent, DialogTitle,
  InputAdornment, List, ListItemButton, ListItemIcon, ListItemText, Stack, TextField, Typography,
} from '@mui/material';

import Iconify from '../../../components/iconify';

AddRecordingDialog.propTypes = {
  open: PropTypes.bool,
  isFirstRecording: PropTypes.bool,
  available: PropTypes.array,
  selectedIds: PropTypes.array,
  search: PropTypes.string,
  onSearchChange: PropTypes.func.isRequired,
  onToggleSelected: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  onAdd: PropTypes.func.isRequired,
};

function formatStartTime(startTime) {
  if (!startTime) return '—';
  const at = new Date(startTime);
  return `${at.toLocaleDateString()} ${at.toLocaleTimeString()}`;
}

/**
 * Picks recordings to pull into the workspace. `available` is already filtered
 * to what the workspace does not hold and ordered newest first; this component
 * only applies the search box on top of it.
 */
export default function AddRecordingDialog({
  open,
  isFirstRecording,
  available = [],
  selectedIds = [],
  search = '',
  onSearchChange,
  onToggleSelected,
  onClose,
  onAdd,
}) {
  const query = search.trim().toLowerCase();
  const matching = query ? available.filter((r) => r.name?.toLowerCase().includes(query)) : available;

  return (
    <Dialog open={!!open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{isFirstRecording ? 'Select Recording(s) to Get Started' : 'Add Recordings'}</DialogTitle>
      <DialogContent>
        <TextField
          size="small"
          placeholder="Search recordings..."
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
          fullWidth
          sx={{ mt: 1, mb: 1 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Iconify icon="eva:search-fill" width={20} sx={{ color: 'text.disabled' }} />
              </InputAdornment>
            ),
          }}
        />

        {selectedIds.length > 0 && (
          <Stack direction="row" spacing={0.5} sx={{ mb: 1, flexWrap: 'wrap', gap: 0.5 }}>
            {selectedIds.map((id) => (
              <Chip
                key={id}
                label={available.find((r) => r.id === id)?.name || id}
                size="small"
                onDelete={() => onToggleSelected(id)}
                color="primary"
                variant="outlined"
              />
            ))}
          </Stack>
        )}

        <List sx={{ maxHeight: 320, overflow: 'auto' }} dense>
          {matching.map((rec) => {
            const isSelected = selectedIds.includes(rec.id);
            return (
              <ListItemButton key={rec.id} selected={isSelected} onClick={() => onToggleSelected(rec.id)}>
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <Checkbox edge="start" checked={isSelected} size="small" tabIndex={-1} disableRipple />
                </ListItemIcon>
                <ListItemText
                  primary={rec.name}
                  secondary={formatStartTime(rec.startTime)}
                  primaryTypographyProps={{ variant: 'body2' }}
                  secondaryTypographyProps={{ variant: 'caption' }}
                />
              </ListItemButton>
            );
          })}
          {matching.length === 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ p: 2, textAlign: 'center' }}>
              {available.length === 0
                ? 'All recordings are already in this workspace.'
                : 'No recordings match your search.'}
            </Typography>
          )}
        </List>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={onAdd} variant="contained" disabled={selectedIds.length === 0}>
          Add {selectedIds.length > 0 ? `(${selectedIds.length})` : ''}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
